// RhymePopover - Popover showing rhyme category details and connected rhymes
// Progressive disclosure: hover shows highlights, click shows full details
// Uses @floating-ui/dom for smart positioning that adapts to viewport edges

import { memo, useEffect, useRef, useState } from 'react'
import { computePosition, flip, shift, offset } from '@floating-ui/dom'
import type { RhymeCategory } from '../../../utils/rhyme/types'
import { CATEGORY_DISPLAY } from '../../../utils/rhyme/categoryDisplay'
import { RHYME_CATEGORY_EXPLANATIONS } from '../../../content/rhyme-category-explanations'
import RhymeCategoryTooltip from '../tooltips/RhymeCategoryTooltip'
import type { ConnectedRhyme } from './RhymeBadge'
import './rhymeBadge.css'

interface RhymePopoverProps {
  /** Rhyme scheme letter (A, B, C, etc.) */
  letter: string
  /** Rhyme category */
  category: RhymeCategory
  /** Whether this is a related rhyme (A' vs A) */
  isRelated: boolean
  /** Reference element to position relative to (the badge) */
  referenceElement: HTMLElement | null
  /** Connected rhymes in this group */
  connectedRhymes: ConnectedRhyme[]
  /** Whether this rhyme matches the stability expectation (defaults to true) */
  isExpected?: boolean
}

/**
 * Custom comparison to prevent unnecessary re-renders
 */
function arePropsEqual(prev: RhymePopoverProps, next: RhymePopoverProps): boolean {
  return (
    prev.letter === next.letter &&
    prev.category === next.category &&
    prev.isRelated === next.isRelated &&
    prev.referenceElement === next.referenceElement &&
    prev.connectedRhymes === next.connectedRhymes &&
    prev.isExpected === next.isExpected
  )
}

/**
 * RhymePopover - Progressive disclosure popover for rhyme details
 *
 * Shows:
 * - Rhyme category name and tier badge
 * - Educational explanation of the rhyme type
 * - Example word pairs for the rhyme category
 * - Stability context (matches expectation vs adds tension)
 * - Connected rhymes panel with line numbers and words
 * - Current line indicator
 *
 * Positioning: Uses @floating-ui/dom with flip() and shift() middleware
 * to intelligently reposition when near viewport edges.
 *
 * Design tokens: docs/design/feedback_system/design_tokens.json
 * Styles: frontend/src/components/lexical/components/rhymeBadge.css
 *
 * Accessibility:
 * - role="dialog" for proper screen reader announcement
 * - aria-labelledby points to header for context
 * - Current line marked with visual indicator
 */
const RhymePopover = memo(function RhymePopover({
  letter,
  category,
  isRelated,
  referenceElement,
  connectedRhymes,
  isExpected = true,
}: RhymePopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  const categoryInfo = CATEGORY_DISPLAY[category]
  const categoryEducation = RHYME_CATEGORY_EXPLANATIONS[category]
  const displayLetter = isRelated ? `${letter}′` : letter
  const headerId = `rhyme-popover-header-${letter}`

  // Get letter color class for badge
  const letterClass = `rhyme-badge--letter-${letter.toLowerCase()}`

  // Get tier class for tier badge
  const tierClass = `rhyme-popover__tier--${categoryInfo.tier.toLowerCase()}`

  // Calculate position using @floating-ui/dom
  // Flips to top if no space below, shifts horizontally to stay in viewport
  useEffect(() => {
    if (!popoverRef.current || !referenceElement) return

    const updatePosition = async () => {
      const { x, y } = await computePosition(referenceElement, popoverRef.current!, {
        placement: 'right-start', // Prefer right of badge, aligned to top
        middleware: [
          offset(8), // 8px gap from badge
          flip({ padding: 16 }), // Flip to left if no space on right
          shift({ padding: 16 }), // Keep 16px from viewport edges
        ],
      })
      setPosition({ x, y })
    }

    updatePosition()

    // Reposition on scroll/resize
    const handleUpdate = () => updatePosition()
    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
    }
  }, [referenceElement])

  // Don't render until we have a reference element
  if (!referenceElement) return null

  return (
    <div
      ref={popoverRef}
      className="rhyme-popover"
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 1000, // High z-index to ensure popover is above stress overlay marks
      }}
      role="dialog"
      aria-labelledby={headerId}
    >
      {/* Header with letter badge and category name */}
      <div className="rhyme-popover__header">
        <div className={`rhyme-popover__badge ${letterClass}`}>{displayLetter}</div>
        <div className="rhyme-popover__type-name" id={headerId}>
          {categoryInfo.name}
        </div>
        <div className={`rhyme-popover__tier ${tierClass}`}>{categoryInfo.tier}</div>
      </div>

      {/* Explanation */}
      <div className="rhyme-popover__description">{categoryEducation.explanation}</div>

      {/* Educational tooltip content */}
      <RhymeCategoryTooltip category={category} isExpected={isExpected} />

      {/* Connected rhymes */}
      <div className="rhyme-popover__connected">
        <div className="rhyme-popover__connected-header">
          Connected Rhymes ({connectedRhymes.length})
        </div>
        {connectedRhymes.map((rhyme, index) => (
          <div key={index} className="rhyme-popover__connected-item">
            <span className="rhyme-popover__line-number">{rhyme.lineNumber}</span>
            <span className="rhyme-popover__word">{rhyme.word}</span>
            {rhyme.isCurrent && (
              <span className="rhyme-popover__current-marker" aria-label="current line">
                ← current
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}, arePropsEqual)

export default RhymePopover
